import React, { createContext, useContext, useState, useEffect } from 'react';
import { useUser } from './UserContext';

interface Product {
  id: string;
  name: string;
  price: number;
  description: string;
  icon: string;
  image: string;
  video?: string;
  stock: number;
  isPreOrder: boolean;
  items: string[];
  category?: string;
  isVessel?: boolean;
  type?: 'product' | 'membership';
}

interface CartItem extends Product {
  quantity: number;
}

interface CartContextType {
  cart: CartItem[];
  addToCart: (product: Product) => void;
  removeFromCart: (id: string) => void;
  updateQuantity: (id: string, delta: number) => void;
  clearCart: () => void;
  cartTotal: number;
  cartTotalOriginal: number;
  cartTotalDiscount: number;
  cartTotalHST: number;
  cartTotalWithTax: number;
  cartCount: number;
  isCartOpen: boolean;
  setIsCartOpen: (isOpen: boolean) => void;
  calculateDiscount: (item: CartItem) => number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, profile, isMember, isAdmin } = useUser();
  const [cart, setCart] = useState<CartItem[]>(() => {
    try {
      const saved = localStorage.getItem('ows_cart');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });
  const [isCartOpen, setIsCartOpen] = useState(false);

  const isUltimateMember = isMember || isAdmin;

  const calculateDiscount = (item: CartItem) => {
    if (isUltimateMember) {
      const itemName = item.name.toLowerCase();
      // Syndicate Member only has 50% off on services (bookings) and board rentals
      const isBoardRental = item.category === 'rental' || itemName.includes('rental');
      if (isBoardRental) {
        return Math.round(item.price * 0.5); // 50% off board rental
      }
    }
    return 0; // Boards, pre-orders, and gear purchases have no member discounts
  };

  const cartTotalOriginal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const cartTotalDiscount = cart.reduce((sum, item) => sum + (calculateDiscount(item) * item.quantity), 0);
  const cartTotal = Math.max(0, cartTotalOriginal - cartTotalDiscount);
  const cartTotalHST = cartTotal * 0.13;
  const cartTotalWithTax = cartTotal + cartTotalHST;
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  // Save to LocalStorage
  useEffect(() => {
    localStorage.setItem('ows_cart', JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product: Product) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item => item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item);
      }
      return [...prev, { ...product, quantity: 1 }];
    });
    setIsCartOpen(true);
  };

  const removeFromCart = (id: string) => {
    setCart(prev => prev.filter(item => item.id !== id));
  };

  const updateQuantity = (id: string, delta: number) => {
    setCart(prev => prev.map(item => {
      if (item.id === id) {
        const newQty = Math.max(1, item.quantity + delta);
        return { ...item, quantity: newQty };
      }
      return item;
    }));
  };

  const clearCart = () => setCart([]);

  return (
    <CartContext.Provider value={{ 
      cart, 
      addToCart, 
      removeFromCart, 
      updateQuantity, 
      clearCart, 
      cartTotal, 
      cartTotalOriginal,
      cartTotalDiscount,
      cartTotalHST,
      cartTotalWithTax,
      cartCount,
      isCartOpen,
      setIsCartOpen,
      calculateDiscount
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
